import { useId, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import {
  GENERAL_LANE,
  STAKEHOLDER_KEYS,
  STAKEHOLDER_LABELS,
  stakeholdersValue,
} from "@/components/application/merchant-tasks/config/options";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { pluralize } from "@/utils/pluralize";

import type { FlowSetting } from "./payload";

type Option = { value: string; label: string };

/** A template's or a jurisdiction's options, as the send dialog lists them. */
function selectOptions(setting: FlowSetting): Option[] {
  if (!Array.isArray(setting.options)) return [];
  return setting.options.flatMap((raw: unknown) => {
    if (!raw || typeof raw !== "object") return [];
    const row = raw as { id?: number; code?: string | null; name?: string };
    if (setting.type === "template" && typeof row.id === "number") {
      return [{ value: String(row.id), label: row.name ?? String(row.id) }];
    }
    if (setting.type === "jurisdiction") {
      return [{ value: row.code ?? GENERAL_LANE, label: row.name ?? "General" }];
    }
    return [];
  });
}

function fromSelect(setting: FlowSetting, raw: string): unknown {
  if (setting.type === "template") return Number(raw);
  return raw === GENERAL_LANE ? null : raw;
}

function toSelect(setting: FlowSetting, value: unknown): string | undefined {
  if (setting.type === "template") return typeof value === "number" ? String(value) : undefined;
  return value == null ? GENERAL_LANE : String(value);
}

interface RowProps {
  /** Unique per card, so two cards in a thread never share a label's target. */
  idPrefix: string;
  setting: FlowSetting;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
}

/** One changed option and its control; "Send signed copies to" stacks its chips under the label. */
function SettingRow({ idPrefix, setting, value, onChange, readOnly }: RowProps) {
  const id = `${idPrefix}-${setting.key}`;
  if (setting.type === "stakeholders") {
    const chosen = stakeholdersValue(value);
    return (
      <li className="space-y-1.5 py-1.5">
        <p id={id} className="text-xs">
          {setting.label}
        </p>
        <div role="group" aria-labelledby={id} className="flex flex-wrap gap-1.5">
          {STAKEHOLDER_KEYS.map((key) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={chosen[key] ? "secondary" : "outline"}
              aria-pressed={chosen[key]}
              disabled={readOnly}
              className={cn(
                "h-6 rounded-full px-2 text-[11px]",
                chosen[key] && "border border-primary/40 bg-accent"
              )}
              onClick={() => onChange({ ...chosen, [key]: !chosen[key] })}
            >
              {STAKEHOLDER_LABELS[key]}
            </Button>
          ))}
        </div>
      </li>
    );
  }
  let control: ReactNode;
  if (setting.type === "checkbox") {
    control = (
      <Switch
        id={id}
        checked={Boolean(value)}
        disabled={readOnly}
        onCheckedChange={(checked) => onChange(checked)}
      />
    );
  } else if (setting.type === "lifetime") {
    control = (
      <Input
        id={id}
        type="number"
        min={1}
        max={365}
        disabled={readOnly}
        value={typeof value === "number" ? value : ""}
        className="h-7 w-20 text-right text-xs"
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next) && next >= 1) onChange(Math.min(365, Math.trunc(next)));
        }}
      />
    );
  } else if (setting.type === "template" || setting.type === "jurisdiction") {
    const options = selectOptions(setting);
    control = (
      <Select
        value={toSelect(setting, value)}
        disabled={readOnly || options.length === 0}
        onValueChange={(raw) => onChange(fromSelect(setting, raw))}
      >
        <SelectTrigger id={id} className="h-7 w-44 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="cursor-pointer text-xs">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  } else {
    control = (
      <span className="text-xs text-muted-foreground">
        {Array.isArray(value) ? value.join(", ") : String(value ?? "")}
      </span>
    );
  }
  return (
    <li className="flex min-h-8 items-center justify-between gap-3 py-1">
      <label htmlFor={id} className="text-xs">
        {setting.label}
      </label>
      {control}
    </li>
  );
}

export interface FlowSettingsProps {
  settings: FlowSetting[];
  /** The user's changes, by key. */
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  readOnly: boolean;
}

/**
 * The options the model changed, behind a quiet "N settings changed" toggle, each editable (the
 * send dialog's field types). Options at their standard value stay out of the card.
 */
export function FlowSettings({ settings, values, onChange, readOnly }: FlowSettingsProps) {
  const idPrefix = useId();
  const [open, setOpen] = useState(false);
  const changed = settings.filter((setting) => setting.changed);
  if (!changed.length) return null;
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(!open)}
      >
        {changed.length} {pluralize(changed.length, "setting")} changed
        <Chevron className="size-3" />
      </button>
      {open ? (
        <ul aria-label="Changed settings" className="mt-1.5 divide-y border-t">
          {changed.map((setting) => (
            <SettingRow
              key={setting.key}
              idPrefix={idPrefix}
              setting={setting}
              value={setting.key in values ? values[setting.key] : setting.value}
              readOnly={readOnly}
              onChange={(value) => onChange(setting.key, value)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
